/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   Thu Mar 9 2023 3:16:54 PM
 *
 *******************************************************/
//flow
import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Card, ButtonGroup, Button, FormLayout, TextContainer, Heading } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { Field, reduxForm } from 'redux-form';

import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import { RenderChoice } from 'src/components/views/common/forms/render-choice';
import { startSABLoading, stopSABLoading } from 'src/integrations/shopify-app-bridge/loading';
import { DashboardDismissChoices as choices } from 'src/constants/dashboard-layout';
import { prettyText } from 'src/helpers';
import * as dashboardActions from 'actions/dashboard-actions';

type Props = {
  loading: boolean,
  success: boolean,
  handleSubmit: () => void,
  pristine: boolean,
  submitting: boolean,
  handleChangeState: () => void
};

type ComposedProps = Props & WithI18nProps;
class Feedback extends Component<ComposedProps> {
  componentWillReceiveProps(nextProps) {
    if (nextProps.loading === true && this.props.loading === false) {
      startSABLoading();
    }

    if (nextProps.loading === false && this.props.loading === true) {
      stopSABLoading();
    }

    if (nextProps.success) {
      this.props.changeItemShow('after-feedback');
    }
  }

  render() {
    const { loading, handleSubmit, pristine, submitting, changeItemShow, i18n } = this.props;

    const formattedChoices = choices.map(function (choice) {
      return {
        ...choice,
        label: i18n.translate(choice.label)
      };
    });

    return (
      <form onSubmit={this.handleDefaultSubmit} className="BC-Feedback-Card__Dashboard">
        <Card>
          <Card.Section>
            <TextContainer>
              <Heading>{i18n.translate('AdminHTML.Common.Action.sendFeedback')}</Heading>

              <FormLayout>
                <Field name="selections" choices={formattedChoices} component={RenderChoice} />
                <Field
                  name="content"
                  placeholder={i18n.translate(
                    'AdminHTML.Dashboard.BlockCanDismiss.feedback_Placeholder'
                  )}
                  component={RenderTextField}
                  anotherProps={{ multiline: 3 }}
                />
                <ButtonGroup>
                  <Button
                    disabled={pristine || submitting}
                    loading={loading && !pristine}
                    onClick={handleSubmit(this.handleSave.bind(this))}>
                    {i18n.translate('AdminHTML.Common.Action.sendFeedback')}
                  </Button>
                  <Button plain onClick={() => changeItemShow('dismiss')}>
                    {i18n.translate('AdminHTML.Common.Action.cancel')}
                  </Button>
                </ButtonGroup>
              </FormLayout>
            </TextContainer>

            <span className="clear" />
          </Card.Section>
        </Card>
      </form>
    );
  }

  // Handle default form submit
  handleDefaultSubmit(e) {
    e.preventDefault();
  }

  //Handle submit
  handleSave = data => {
    const { sendFeedback, blockName, i18n } = this.props;
    let { selections, content } = _.pick(data, ['selections', 'content']);

    // Get selection text
    const select = selections[0] || '';
    const selectionText = {
      userful: i18n.translate('AdminHTML.Dashboard.BlockCanDismiss.feedback_OptionUserful'),
      notUseful: i18n.translate('AdminHTML.Dashboard.BlockCanDismiss.feedback_OptionNotUseful'),
      appearOften: i18n.translate('AdminHTML.Dashboard.BlockCanDismiss.feedback_OptionAppearOften')
    };

    const feedback = {
      selectionText: selectionText[select] || '',
      content: prettyText(content)
    };

    sendFeedback(feedback, blockName);
  };
}

const mapStateToProps = (state, props) => ({
  loading: _.indexOf(state.dashboard.feedbackLoading, props.blockName) !== -1,
  success: _.indexOf(state.dashboard.feedbackSuccess, props.blockName) !== -1,
  error: state.dashboard.error,
  form: `Feedback_${props.blockName}` // a unique identifier for this form
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  sendFeedback: (body: Object, blockName: string) =>
    dispatch(dashboardActions.sendFeedback(body, blockName, ownProps.i18n))
});

const FeedbackForm = reduxForm({
  enableReinitialize: true
})(Feedback);

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(FeedbackForm));
